"use client";

import { useState } from "react";

interface MatchLobbyProps {
  shareLink: string;
  selfName: string;
  onCancel: () => void;
}

// Shown to the host while waiting for a friend to join the match channel.
export default function MatchLobby({
  shareLink,
  selfName,
  onCancel,
}: MatchLobbyProps) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
    } catch {
      /* clipboard may be blocked; the field is selectable as a fallback */
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-6 px-4 text-center">
      <div className="text-[clamp(0.7rem,1.3vw,0.875rem)] uppercase tracking-widest text-white/40">
        Playing as {selfName}
      </div>
      <h1 className="text-[clamp(1.5rem,4vw,2.5rem)] font-extrabold">
        Waiting for a friend…
      </h1>
      <p className="max-w-md text-white/60">
        Send this link to a friend. The match starts the moment they open it —
        you&apos;ll both spin at the same time.
      </p>

      <div className="flex w-full max-w-md flex-col gap-3">
        <div className="flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-4 py-3">
          <input
            readOnly
            value={shareLink}
            onFocus={(e) => e.currentTarget.select()}
            className="w-full select-all bg-transparent text-sm text-white/80 outline-none"
          />
        </div>
        <button
          onClick={copy}
          className="rounded-full bg-accent px-8 py-3 text-sm font-bold uppercase tracking-widest text-white shadow-[0_0_30px_rgba(124,58,237,0.5)] transition hover:brightness-110"
        >
          {copied ? "Link copied!" : "Copy Link"}
        </button>
      </div>

      <div className="mt-2 flex items-center gap-2 text-sm text-white/40">
        <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-accent" />
        Waiting for opponent to connect
      </div>

      <button
        onClick={onCancel}
        className="mt-4 text-xs font-bold uppercase tracking-widest text-white/40 underline-offset-4 hover:text-white/70 hover:underline"
      >
        Cancel and play solo
      </button>
    </main>
  );
}
